import { useMemo, useRef } from 'react'
import { makeCursorRows } from '../../../../api-new/state/notify'
import { serverMessage } from '../../../../shared/error/server-message'
import { useErrorLine } from '../../../../shared/error/use-error-line'

// A TableAgInfinite datasource over a C-4 cursor list, for one set of
// filters (`key`; a new key is a new grid). load({ limit, cursor, search })
// asks for one page. Each search gets its own cursor chain; a response for
// a key the page has left never lands. A failure is the page's error line
// with its reference, and ends the list where it is.
export const useCursorGrid = ({ key, load, failed = "This list couldn't be loaded.", olderFailed = "Older rows couldn't be loaded." }) => {
  const [error, failure, setError] = useErrorLine()
  const currentKey = useRef(key)
  currentKey.current = key
  const loadRef = useRef(load)
  loadRef.current = load

  const getRows = useMemo(
    () => {
      const mine = key
      const chains = new Map()
      const chainFor = search => {
        if (!chains.has(search)) {
          chains.set(search, makeCursorRows({
            isCurrent: () => currentKey.current === mine,
            load: ({ limit, cursor }) => loadRef.current({ limit, cursor, search })
          }))
        }
        return chains.get(search)
      }
      return async (start, end, search) => {
        try {
          setError(null)
          return await chainFor((search || '').trim())(start, end)
        } catch (err) {
          if (currentKey.current !== mine) return undefined
          setError(serverMessage(err, start === 0 ? failed : olderFailed), err)
          return { data: [], count: start }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  )

  return { getRows, error, failure }
}

// The grid's own ref, refreshed in place after an action (no new grid,
// the scroll position kept).
export const refreshGrid = tableRef => {
  if (tableRef && tableRef.current && tableRef.current.api) tableRef.current.api.refreshInfiniteCache()
}
